import React, { useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_KEYS = ["Framing", "Drywall", "Painting", "Finishing", "Acoustic Ceilings", "Flooring", "Other"];

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", address: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error(t.contact.errorTitle, { description: "Please fill name, email, phone and service." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/estimates`, form);
      toast.success(t.contact.successTitle, { description: t.contact.successDesc });
      setForm({ name: "", email: "", phone: "", service: "", address: "", message: "" });
    } catch (err) {
      toast.error(t.contact.errorTitle, { description: t.contact.errorDesc });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-black py-24 sm:py-32 border-t border-neutral-900">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 text-orange-500 font-mono-label text-xs uppercase tracking-[0.25em] mb-5">
            <span className="w-8 h-px bg-orange-500" /> {t.contact.label}
          </div>
          <h2 className="font-display uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-neutral-400 text-base sm:text-lg leading-relaxed max-w-md">{t.contact.sub}</p>

          <div className="mt-10 space-y-5">
            <a href="tel:+12055383654" data-testid="contact-phone" className="group flex items-center gap-4 border-b border-neutral-900 pb-5">
              <div className="w-11 h-11 grid place-items-center bg-orange-500/10 text-orange-500 rounded-sm group-hover:bg-orange-500 group-hover:text-black transition-colors">
                <Phone size={18} />
              </div>
              <div>
                <div className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-neutral-500">{t.contact.phoneLabel}</div>
                <div className="font-display uppercase text-white text-xl">(205) 538-3654</div>
              </div>
            </a>
            <a href="mailto:blackrockconstructionservicess@gmail.com" data-testid="contact-email" className="group flex items-center gap-4 border-b border-neutral-900 pb-5">
              <div className="w-11 h-11 grid place-items-center bg-orange-500/10 text-orange-500 rounded-sm group-hover:bg-orange-500 group-hover:text-black transition-colors">
                <Mail size={18} />
              </div>
              <div>
                <div className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-neutral-500">{t.contact.emailLabel}</div>
                <div className="text-white text-sm sm:text-base break-all">blackrockconstructionservicess@gmail.com</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 grid place-items-center bg-orange-500/10 text-orange-500 rounded-sm">
                <MapPin size={18} />
              </div>
              <div>
                <div className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-neutral-500">{t.contact.locationLabel}</div>
                <div className="text-white text-base">Birmingham, Alabama</div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="estimate-form"
          className="lg:col-span-7 bg-neutral-950 border border-neutral-900 rounded-sm p-6 sm:p-10"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Label htmlFor="name" className="text-neutral-400 font-mono-label uppercase text-[10px] tracking-[0.2em]">{t.contact.name} *</Label>
              <Input
                id="name"
                data-testid="form-name"
                value={form.name}
                onChange={update("name")}
                required
                className="mt-2 bg-black border-neutral-800 text-white focus-visible:ring-orange-500 focus-visible:border-orange-500 rounded-sm h-12"
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-neutral-400 font-mono-label uppercase text-[10px] tracking-[0.2em]">{t.contact.email} *</Label>
              <Input
                id="email"
                type="email"
                data-testid="form-email"
                value={form.email}
                onChange={update("email")}
                required
                className="mt-2 bg-black border-neutral-800 text-white focus-visible:ring-orange-500 focus-visible:border-orange-500 rounded-sm h-12"
                placeholder="you@email.com"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-neutral-400 font-mono-label uppercase text-[10px] tracking-[0.2em]">{t.contact.phone} *</Label>
              <Input
                id="phone"
                data-testid="form-phone"
                value={form.phone}
                onChange={update("phone")}
                required
                className="mt-2 bg-black border-neutral-800 text-white focus-visible:ring-orange-500 focus-visible:border-orange-500 rounded-sm h-12"
                placeholder="(205) 000-0000"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-neutral-400 font-mono-label uppercase text-[10px] tracking-[0.2em]">{t.contact.service} *</Label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger
                  data-testid="form-service"
                  className="mt-2 bg-black border-neutral-800 text-white h-12 rounded-sm focus:ring-orange-500"
                >
                  <SelectValue placeholder={t.contact.pickService} />
                </SelectTrigger>
                <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                  {SERVICE_KEYS.map((k, i) => (
                    <SelectItem key={k} value={k} data-testid={`service-option-${i}`} className="focus:bg-orange-500/20 focus:text-orange-400">
                      {t.services.items[i]?.name || k}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="address" className="text-neutral-400 font-mono-label uppercase text-[10px] tracking-[0.2em]">{t.contact.address}</Label>
              <Input
                id="address"
                data-testid="form-address"
                value={form.address}
                onChange={update("address")}
                className="mt-2 bg-black border-neutral-800 text-white focus-visible:ring-orange-500 focus-visible:border-orange-500 rounded-sm h-12"
                placeholder="123 Main St, Birmingham, AL"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="message" className="text-neutral-400 font-mono-label uppercase text-[10px] tracking-[0.2em]">{t.contact.message}</Label>
              <Textarea
                id="message"
                data-testid="form-message"
                value={form.message}
                onChange={update("message")}
                rows={5}
                className="mt-2 bg-black border-neutral-800 text-white focus-visible:ring-orange-500 focus-visible:border-orange-500 rounded-sm resize-none"
                placeholder="Tell us about your project, timeline, square footage, etc."
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="form-submit"
            disabled={submitting}
            className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-black font-bold uppercase tracking-wider text-sm px-8 py-4 rounded-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(249,115,22,0.35)]"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" /> {t.contact.sending}
              </>
            ) : (
              <>
                {t.contact.submit} <Send size={16} />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
